/**
 * 정산 내역 관리 목록.
 * admin-ruda-web `prescriptionSettlements.js` 포팅.
 *
 * 레거시 탭(사용자별/제약사별/병의원별)은 삭제하고 사용자별 단일 목록으로 유지.
 * 제약사 정산 상태별 라디오 필터 추가 (레거시 radioFilter 동일).
 */
import {
  useDeleteUserSettlement,
  useExportUserSettlements,
  useGenerateSettlement,
  useMergePharmaSettlement,
  usePublishSettlement,
  useSettlementStatus,
  useUserSettlementList,
} from '@/entities/settlement/hooks/useSettlements';
import type { SettlementUserInfoResponse } from '@/entities/settlement/api/settlement.api';
import { EmptyState } from '@/shared/components/EmptyState';
import { DataCard } from '@/shared/components/DataCard';
import { MonthPicker } from '@/shared/components/DatePicker';
import { FilterCard } from '@/shared/components/FilterCard';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { PageHeader } from '@/shared/components/PageHeader';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { settlementPrescriptionDetailPath } from '@/shared/constants/routes';
import { useListSearchParams } from '@/shared/hooks/useListSearchParams';
import { SettlementStatusBanner } from '@/pages/settlements/SettlementStatusBanner';
import {
  formatSettlementAmount,
  formatSettlementYearMonth,
  getDefaultSettlementYm,
} from '@/pages/settlements/settlementUtils';
import { downloadSettlementUserDetailsCsv } from '@/pages/settlements/settlementExportUtils';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useNavigate } from '@tanstack/react-router';
import { Banknote, Download, Trash2 } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { ToolbarRow } from '@/shared/components/ui/row';

const getUserSettlementRowKey = (row: SettlementUserInfoResponse) =>
  `${row.userId}-${row.prescriptionYearMonth}-${row.settlementYearMonth}`;

/** 레거시 radioFilter — 제약사 정산 상태별 필터 */
const APPROVAL_RADIO_OPTIONS = [
  { label: '전체', value: 'all' },
  { label: '승인', value: '승인' },
  { label: '미등록 거래처', value: '미등록_거래처' },
  { label: '최소금액기준 미달', value: '최소금액기준_미달' },
  { label: '거래 불가 의약품', value: '거래_불가_의약품' },
  { label: '흡수율 미달', value: '흡수율_미달' },
] as const;

type ApprovalValue = (typeof APPROVAL_RADIO_OPTIONS)[number]['value'];

const PRESCRIPTION_SETTLEMENT_SEARCH_DEFAULTS = {
  page: 1,
  pageSize: 25,
  settlementYearMonth: getDefaultSettlementYm(),
  email: '',
  companyName: '',
  ceoName: '',
  settlementStatus: 'all' as ApprovalValue,
};

export const PrescriptionSettlementsPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();

  const { values, setValues } = useListSearchParams(PRESCRIPTION_SETTLEMENT_SEARCH_DEFAULTS);

  const [yearMonthInput, setYearMonthInput] = useState(values.settlementYearMonth);
  const [emailInput, setEmailInput] = useState(values.email);
  const [companyNameInput, setCompanyNameInput] = useState(values.companyName);
  const [ceoNameInput, setCeoNameInput] = useState(values.ceoName);

  useEffect(() => {
    setYearMonthInput(values.settlementYearMonth);
    setEmailInput(values.email);
    setCompanyNameInput(values.companyName);
    setCeoNameInput(values.ceoName);
  }, [values.settlementYearMonth, values.email, values.companyName, values.ceoName]);

  const search = useMemo(
    () => ({
      settlementYearMonth: values.settlementYearMonth,
      email: values.email || undefined,
      companyName: values.companyName || undefined,
      ceoName: values.ceoName || undefined,
      settlementStatus:
        values.settlementStatus === 'all' ? undefined : values.settlementStatus,
    }),
    [values],
  );

  const { data: status, isLoading: statusLoading } =
    useSettlementStatus(values.settlementYearMonth);

  const { data: userRows = [], isLoading, isError, error } =
    useUserSettlementList(search);

  const generateMutation = useGenerateSettlement();
  const mergeMutation = useMergePharmaSettlement();
  const publishMutation = usePublishSettlement();
  const exportMutation = useExportUserSettlements();
  const deleteSettlementMutation = useDeleteUserSettlement();

  const [selectedRowKeys, setSelectedRowKeys] = useState<Set<string>>(
    () => new Set(),
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    setSelectedRowKeys(new Set());
  }, [userRows]);

  useEffect(() => {
    if (isError && error) {
      handleApiError(error, toast);
    }
  }, [isError, error, toast]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setValues({
      settlementYearMonth: yearMonthInput.trim() || getDefaultSettlementYm(),
      email: emailInput.trim(),
      companyName: companyNameInput.trim(),
      ceoName: ceoNameInput.trim(),
      page: 1,
    });
  };

  const handleYearMonthChange = (nextYearMonth: string) => {
    const normalizedYearMonth = nextYearMonth.trim() || getDefaultSettlementYm();
    setYearMonthInput(normalizedYearMonth);
    setValues({
      settlementYearMonth: normalizedYearMonth,
      page: 1,
    });
  };

  const runAction = async (label: string, fn: () => Promise<void>) => {
    try {
      await fn();
      toast.success(`${label}을(를) 완료했습니다.`);
    } catch (err) {
      handleApiError(err, toast);
    }
  };

  const handleExportAll = async () => {
    try {
      const rows = await exportMutation.mutateAsync({
        settlementYearMonth: values.settlementYearMonth,
      });
      if (rows.length === 0) {
        toast.error('다운로드할 정산 정보가 없습니다.');
        return;
      }
      downloadSettlementUserDetailsCsv(rows, values.settlementYearMonth);
      toast.success('정산 정보를 다운로드했습니다.');
    } catch (err) {
      handleApiError(err, toast);
    }
  };

  const handleExportByEmail = async () => {
    const email = values.email.trim();
    if (!email) {
      toast.error('사용자 이메일을 입력해 주세요.');
      return;
    }

    try {
      const rows = await exportMutation.mutateAsync({
        settlementYearMonth: values.settlementYearMonth,
        email,
      });
      if (rows.length === 0) {
        toast.error('다운로드할 정산 정보가 없습니다.');
        return;
      }
      downloadSettlementUserDetailsCsv(rows, values.settlementYearMonth);
      toast.success('이메일 검색 다운로드를 완료했습니다.');
    } catch (err) {
      handleApiError(err, toast);
    }
  };

  const selectedRows = useMemo(
    () =>
      userRows.filter((row) => selectedRowKeys.has(getUserSettlementRowKey(row))),
    [userRows, selectedRowKeys],
  );

  const toggleRowSelection = (rowKey: string) => {
    setSelectedRowKeys((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) {
        next.delete(rowKey);
      } else {
        next.add(rowKey);
      }
      return next;
    });
  };

  const toggleAllRows = () => {
    if (selectedRowKeys.size === userRows.length) {
      setSelectedRowKeys(new Set());
      return;
    }
    setSelectedRowKeys(new Set(userRows.map(getUserSettlementRowKey)));
  };

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      return;
    }

    let deletedCount = 0;
    try {
      for (const row of selectedRows) {
        await deleteSettlementMutation.mutateAsync({
          prescriptionYearMonth: row.prescriptionYearMonth,
          settlementYearMonth: row.settlementYearMonth,
          userId: row.userId,
        });
        deletedCount += 1;
      }
      setSelectedRowKeys(new Set());
      setDeleteDialogOpen(false);
      toast.success(`${deletedCount}건의 정산 내역을 삭제했습니다.`);
    } catch (err) {
      setDeleteDialogOpen(false);
      if (deletedCount > 0) {
        setSelectedRowKeys((prev) => {
          const next = new Set(prev);
          selectedRows.slice(0, deletedCount).forEach((row) => {
            next.delete(getUserSettlementRowKey(row));
          });
          return next;
        });
        toast.error(
          `${deletedCount}건 삭제 후 오류가 발생했습니다. 나머지 항목을 확인해 주세요.`,
        );
        return;
      }
      handleApiError(err, toast);
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Banknote}
        title="정산 내역 관리"
        description="사용자별 처방 정산 내역 조회 및 관리"
        actions={
          <ToolbarRow gap={8}>
            <MenuWriteGate>
              <Button
                size="sm"
                className="ruda-button"
                disabled={generateMutation.isPending}
                onClick={() =>
                  runAction('정산 정보 생성', () =>
                    generateMutation.mutateAsync(values.settlementYearMonth),
                  )
                }
              >
                정산 정보 생성
              </Button>
              <Button
                size="sm"
                className="ruda-button"
                disabled={mergeMutation.isPending}
                onClick={() =>
                  runAction('제약사 정산정보 업데이트', () =>
                    mergeMutation.mutateAsync(values.settlementYearMonth),
                  )
                }
              >
                제약사 정산정보 업데이트
              </Button>
              <Button
                size="sm"
                className="ruda-button"
                disabled={publishMutation.isPending}
                onClick={() =>
                  runAction('정산 데이터 게시', () =>
                    publishMutation.mutateAsync(values.settlementYearMonth),
                  )
                }
              >
                정산 데이터 게시
              </Button>
              <Button
                size="sm"
                variant="destructive"
                disabled={
                  selectedRows.length === 0 ||
                  deleteSettlementMutation.isPending
                }
                onClick={() => setDeleteDialogOpen(true)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                선택한 정산 삭제 ({selectedRows.length})
              </Button>
            </MenuWriteGate>
            {!!values.email.trim() && (
              <Button
                size="sm"
                variant="outline"
                disabled={exportMutation.isPending}
                onClick={handleExportByEmail}
              >
                <Download className="mr-2 h-4 w-4" />
                이메일 검색 다운로드
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              disabled={exportMutation.isPending}
              onClick={handleExportAll}
            >
              <Download className="mr-2 h-4 w-4" />
              전체 내역 다운로드
            </Button>
          </ToolbarRow>
        }
      />

      <FilterCard>
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-[minmax(0,240px)_auto] lg:items-end">
            <div className="space-y-2">
              <Label htmlFor="settlementYm">정산 년월</Label>
              <MonthPicker
                id="settlementYm"
                value={yearMonthInput}
                onChange={handleYearMonthChange}
                placeholder="년월 선택"
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-2">
              <Label htmlFor="email">사용자 이메일</Label>
              <Input
                id="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="사용자 이메일"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="companyName">업체명</Label>
              <Input
                id="companyName"
                value={companyNameInput}
                onChange={(e) => setCompanyNameInput(e.target.value)}
                placeholder="업체명"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ceoName">대표자명</Label>
              <Input
                id="ceoName"
                value={ceoNameInput}
                onChange={(e) => setCeoNameInput(e.target.value)}
                placeholder="사용자 이름"
              />
            </div>
            <div className="flex items-end">
              <Button type="submit" className="ruda-button w-full sm:w-auto">
                검색
              </Button>
            </div>
          </div>

          {/* 레거시 radioFilter — 제약사 정산 상태별 승인 결과 */}
          <div className="space-y-2">
            <Label>제약사 정산 상태별 조회</Label>
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              {APPROVAL_RADIO_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex cursor-pointer items-center gap-1.5 text-sm">
                  <input
                    type="radio"
                    name="settlementStatus"
                    value={opt.value}
                    checked={values.settlementStatus === opt.value}
                    onChange={() =>
                      setValues({ settlementStatus: opt.value as ApprovalValue, page: 1 })
                    }
                    className="accent-primary"
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>
        </form>
      </FilterCard>

      <SettlementStatusBanner
        settlementYearMonth={values.settlementYearMonth}
        status={status}
        isLoading={statusLoading}
      />

      <DataCard>
        {isLoading ? (
          <LoadingSpinner />
        ) : userRows.length === 0 ? (
          <EmptyState description="정산 내역 데이터가 없습니다." />
        ) : (
          <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <MenuWriteGate>
                      <input
                        type="checkbox"
                        checked={
                          userRows.length > 0 &&
                          selectedRowKeys.size === userRows.length
                        }
                        onChange={toggleAllRows}
                        aria-label="전체 선택"
                      />
                    </MenuWriteGate>
                  </TableHead>
                  <TableHead>처방 년월</TableHead>
                  <TableHead>정산 년월</TableHead>
                  <TableHead>사용자 이메일</TableHead>
                  <TableHead>대표자</TableHead>
                  <TableHead>연락처</TableHead>
                  <TableHead className="text-right">정산 금액</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {userRows.map((row) => {
                  const rowKey = getUserSettlementRowKey(row);
                  const isSelected = selectedRowKeys.has(rowKey);

                  return (
                    <TableRow
                      key={rowKey}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() =>
                        navigate({
                          to: settlementPrescriptionDetailPath(row.email),
                          search: {
                            prescriptionYm: row.prescriptionYearMonth,
                            settlementYm: row.settlementYearMonth,
                            userId: row.userId,
                          },
                        } as never)
                      }
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <MenuWriteGate>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleRowSelection(rowKey)}
                            aria-label={`${row.email} 정산 선택`}
                          />
                        </MenuWriteGate>
                      </TableCell>
                      <TableCell>
                        {formatSettlementYearMonth(row.prescriptionYearMonth)}
                      </TableCell>
                      <TableCell>
                        {formatSettlementYearMonth(row.settlementYearMonth)}
                      </TableCell>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>{row.ceoName}</TableCell>
                      <TableCell>{row.phoneNumber}</TableCell>
                      <TableCell className="text-right">
                        {formatSettlementAmount(row.totalSettlementAmount)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
          </Table>
        )}
      </DataCard>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>선택한 정산 내역 삭제</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  선택한 {selectedRows.length}건의 정산 내역을 순서대로
                  삭제합니다.
                </p>
                <p>
                  각 항목의 settlement 행이 모두 삭제되며, 제약사 원천
                  데이터(settlement_pharma)는 유지됩니다.
                </p>
                <p>
                  삭제 후 복구하려면 정산 정보 생성과 제약사 정산정보 업데이트를
                  다시 실행해야 합니다. 공개된 정산도 삭제됩니다.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteSettlementMutation.isPending}
              onClick={() => void handleBulkDelete()}
            >
              삭제
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
